import React, { useState, useRef } from 'react';
import './FormPanel.css';
import { suggestSummary, suggestBullets, analyzeJobMatch, suggestPageFit } from '../ai';
import PhotoCropModal from './PhotoCropModal';

const SECTIONS = [
  { id: 'personal',   label: 'Personal',   icon: '👤' },
  { id: 'experience', label: 'Experience', icon: '💼' },
  { id: 'education',  label: 'Education',  icon: '🎓' },
  { id: 'skills',     label: 'Skills',     icon: '⚡' },
  { id: 'misc',       label: 'Misc',       icon: '📋' },
  { id: 'jobmatch',   label: 'Job Match',  icon: '🎯' },
  { id: 'page',       label: 'Page Setup', icon: '📐' },
];

export default function FormPanel({
  resume, activeSection, setActiveSection,
  updatePersonal,
  addExperience, updateExperience, removeExperience, removeBullet,
  addSubRole, updateSubRole, removeSubRole,
  addEducation, updateEducation, removeEducation,
  addSkill, removeSkill, reorderSkills,
  addMiscSection, updateMiscSection, updateMiscCiteFmt, removeMiscSection,
  addMiscEntry, updateMiscEntry, removeMiscEntry,
  sectionStyles, updateSectionStyle,
  pageSettings, updatePageSetting,
  templateFeatures = { photo: false, photoPosition: false },
  templateDefaultStyles,
  templateDefaultPageSettings,
  densityPresets = [],
  applyDensityPreset,
  selectedDensityLabel,
  batchSectionStyle,
  batchPageSettings,
  updatePageSettingLive,
  commitPageSettingHistory,
  reorderSections,
  toggleSectionVisibility,
  onResetToTemplateDefaults,
  applyBulletSuggestion,
  applySummarySuggestion,
  applyAIBullets,
  updateExperienceWithHistory,
  numPages,
}) {
  const [showResetConfirm,        setShowResetConfirm]        = useState(false);
  const [skillSuggestions,        setSkillSuggestions]        = useState([]);
  const [skillSuggestionsLoading, setSkillSuggestionsLoading] = useState(false);
  const skillSuggestionsFetched = React.useRef(false);

  // Fetch once when we have enough experience data
  React.useEffect(() => {
    if (skillSuggestionsFetched.current) return;
    const hasEnoughData = (resume.experience || []).some(e => e.role || e.company || (e.subRoles || []).length > 0);
    if (!hasEnoughData) return;
    skillSuggestionsFetched.current = true;
    setSkillSuggestionsLoading(true);
    import('../ai').then(({ suggestSkills }) =>
      suggestSkills({ experience: resume.experience, personal: resume.personal, existingSkills: resume.skills })
        .then(s => setSkillSuggestions(s))
        .catch(() => setSkillSuggestions([]))
        .finally(() => setSkillSuggestionsLoading(false))
    );
  }, [resume.experience]); // eslint-disable-line

  return (
    <>
      {showResetConfirm && (
        <div className="nudge-overlay" onClick={() => setShowResetConfirm(false)}>
          <div className="nudge-modal" onClick={e => e.stopPropagation()}>
            <div className="nudge-icon">↺</div>
            <h2 className="nudge-title">Reset all styles?</h2>
            <p className="nudge-body">This will reset all formatting — personal, experience, skills, and page settings — back to the template defaults. This cannot be undone.</p>
            <div className="nudge-actions">
              <button className="btn-nudge-resume" onClick={() => { onResetToTemplateDefaults(); setShowResetConfirm(false); }}>Yes, reset everything</button>
              <button className="btn-nudge-proceed" onClick={() => setShowResetConfirm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    <div className="form-panel">
      <nav className="section-nav">
        {SECTIONS.map(s => (
          <button
            key={s.id}
            className={`nav-btn ${activeSection === s.id ? 'active' : ''}`}
            onClick={() => setActiveSection(s.id)}
          >
            <span className="nav-icon">{s.icon}</span>
            <span>{s.label}</span>
          </button>
        ))}
      </nav>

      <div className="form-content">
        <div style={{ display: activeSection === 'personal' ? undefined : 'none' }}>
          <PersonalForm
            data={resume.personal}
            update={updatePersonal}
            styles={sectionStyles.personal}
            updateStyle={(field, value) => updateSectionStyle('personal', field, value)}
            resume={resume}
            templateFeatures={templateFeatures}
            batchSectionStyle={batchSectionStyle}
            onAcceptAISummary={applySummarySuggestion}
          />
        </div>
        <div style={{ display: activeSection === 'experience' ? undefined : 'none' }}>
          <ExperienceForm
            items={resume.experience}
            add={addExperience}
            update={updateExperience}
            remove={removeExperience}
            removeBullet={removeBullet}
            addSubRole={addSubRole}
            updateSubRole={updateSubRole}
            removeSubRole={removeSubRole}
            styles={sectionStyles.experience}
            updateStyle={(field, value) => updateSectionStyle('experience', field, value)}
            defaultStyles={templateDefaultStyles?.experience}
            batchSectionStyle={batchSectionStyle}
            applyAIBullets={applyAIBullets}
            updateExperienceWithHistory={updateExperienceWithHistory}
          />
        </div>
        <div style={{ display: activeSection === 'education' ? undefined : 'none' }}>
          <EducationForm
            items={resume.education}
            add={addEducation}
            update={updateEducation}
            remove={removeEducation}
            styles={sectionStyles.education}
            updateStyle={(field, value) => updateSectionStyle('education', field, value)}
            defaultStyles={templateDefaultStyles?.education}
            batchSectionStyle={batchSectionStyle}
          />
        </div>
        <div style={{ display: activeSection === 'skills' ? undefined : 'none' }}>
          <SkillsForm
            skills={resume.skills}
            add={addSkill}
            remove={removeSkill}
            reorder={reorderSkills}
            styles={sectionStyles.skills}
            updateStyle={(field, value) => updateSectionStyle('skills', field, value)}
            defaultStyles={templateDefaultStyles?.skills}
            templateFeatures={templateFeatures}
            suggestions={skillSuggestions}
            suggestionsLoading={skillSuggestionsLoading}
            batchSectionStyle={batchSectionStyle}
          />
        </div>
        <div style={{ display: activeSection === 'misc' ? undefined : 'none' }}>
          <MiscForm
            sections={resume.misc || []}
            addSection={addMiscSection}
            updateSection={updateMiscSection}
            removeSection={removeMiscSection}
            addEntry={addMiscEntry}
            updateEntry={updateMiscEntry}
            removeEntry={removeMiscEntry}
            styles={sectionStyles.misc}
            updateStyle={(field, value) => updateSectionStyle('misc', field, value)}
            defaultStyles={templateDefaultStyles?.misc}
            updateMiscCiteFmt={updateMiscCiteFmt}
          />
        </div>
        <div style={{ display: activeSection === 'jobmatch' ? undefined : 'none' }}>
          <JobMatchForm
            resume={resume}
            addSkill={addSkill}
            applyBulletSuggestion={applyBulletSuggestion}
            applySummarySuggestion={applySummarySuggestion}
          />
        </div>
        <div style={{ display: activeSection === 'page' ? undefined : 'none' }}>
          <PageSetupForm settings={pageSettings} update={updatePageSetting} updateLive={updatePageSettingLive} commitHistory={commitPageSettingHistory} templateFeatures={templateFeatures} defaultPageSettings={templateDefaultPageSettings} densityPresets={densityPresets} applyDensityPreset={applyDensityPreset} sectionStyles={sectionStyles} selectedDensityLabel={selectedDensityLabel} batchPageSettings={batchPageSettings} reorderSections={reorderSections} toggleSectionVisibility={toggleSectionVisibility} resume={resume} onMasterReset={() => setShowResetConfirm(true)} numPages={numPages} applyBulletSuggestion={applyBulletSuggestion} applySummarySuggestion={applySummarySuggestion} removeSkill={removeSkill} />
        </div>
      </div>
    </div>
    </>
  );
}

// ── Personal ──────────────────────────────────────────────
function PersonalForm({ data, update, styles, updateStyle, resume, templateFeatures = {}, onAcceptAISummary }) {
  const [styleOpen, setStyleOpen] = useState(false);
  const [suggestingSum, setSuggestingSum] = useState(false);
  const [suggestedSummary, setSuggestedSummary] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const photoRef = useRef();
  const showPhoto = templateFeatures.photo === true;

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCropSrc(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCropSave = (croppedImage) => {
    update('photo', croppedImage);
    setCropSrc(null);
  };

  const handleSuggestSummary = async () => {
    setSuggestingSum(true);
    try {
      const summary = await suggestSummary({
        name: data.name, title: data.title,
        skills: resume.skills, experience: resume.experience,
        existingSummary: data.summary,
      });
      setSuggestedSummary(summary);
    } catch (err) {
      alert('AI suggestion failed: ' + err.message);
    } finally {
      setSuggestingSum(false);
    }
  };

  const acceptSummary  = () => { onAcceptAISummary(suggestedSummary); setSuggestedSummary(null); };
  const discardSummary = () => setSuggestedSummary(null);

  const reset = () => {
    if (batchSectionStyle) {
      batchSectionStyle('personal', {
        headerAlign: 'left', showIcons: true, iconColor: 'default',
        headerFont: 'DM Serif Display', headerNameSize: 32, headerContactSize: 12,
      });
    } else {
      updateStyle('headerAlign', 'left');
      updateStyle('showIcons', true);
      updateStyle('iconColor', 'default');
      updateStyle('headerFont', 'DM Serif Display');
      updateStyle('headerNameSize', 32);
      updateStyle('headerContactSize', 12);
    }
  };

  return (
    <div className="form-section">
      {cropSrc && (
        <PhotoCropModal imageSrc={cropSrc} onSave={handleCropSave} onCancel={() => setCropSrc(null)} />
      )}
      <h2 className="section-heading">Personal Information</h2>

      <div className="style-toolbar">
        <button className="style-toolbar-toggle" onClick={() => setStyleOpen(o => !o)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/>
          </svg>
          Style Options
          <span className="style-toolbar-chevron">{styleOpen ? '▲' : '▼'}</span>
        </button>
        {styleOpen && (
          <div className="style-toolbar-body">
            {templateFeatures.headerAlign !== false && (
              <div className="style-row">
                <label className="style-label">Header Alignment</label>
                <div className="align-toggle">
                  {['left', 'center', 'right'].map(align => (
                    <button key={align} className={`align-btn ${styles.headerAlign === align ? 'active' : ''}`} onClick={() => updateStyle('headerAlign', align)} title={align.charAt(0).toUpperCase() + align.slice(1)}>
                      {align === 'left'   && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="18" y2="18"/></svg>}
                      {align === 'center' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>}
                      {align === 'right'  && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="6" y1="18" x2="21" y2="18"/></svg>}
                      <span>{align.charAt(0).toUpperCase() + align.slice(1)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="style-row">
              <label className="style-label">Name Font</label>
              <select className="style-select" value={styles.headerFont ?? 'DM Serif Display'} onChange={e => updateStyle('headerFont', e.target.value)}>
                <option value="DM Serif Display">DM Serif Display</option>
                <option value="Georgia">Georgia</option>
                <option value="Times New Roman">Times New Roman</option>
                <option value="DM Sans">DM Sans</option>
                <option value="Helvetica, Arial, sans-serif">Helvetica</option>
                <option value="Garamond, serif">Garamond</option>
                <option value="Courier New, monospace">Courier New</option>
              </select>
            </div>
            <div className="style-row">
              <label className="style-label">Name Size <EditableValue value={styles.headerNameSize ?? 32} min={18} max={48} step={1} unit="px" decimals={0} onChange={v => updateStyle('headerNameSize', v)} /></label>
              <input type="range" min={18} max={48} step={1} value={styles.headerNameSize ?? 32} onChange={e => updateStyle('headerNameSize', parseInt(e.target.value))} className="style-slider" />
            </div>
            <div className="style-row">
              <label className="style-label">Contact Size <EditableValue value={styles.headerContactSize ?? 12} min={9} max={16} step={0.5} unit="px" decimals={1} onChange={v => updateStyle('headerContactSize', v)} /></label>
              <input type="range" min={9} max={16} step={0.5} value={styles.headerContactSize ?? 12} onChange={e => updateStyle('headerContactSize', parseFloat(e.target.value))} className="style-slider" />
            </div>
            <div className="style-row">
              <label className="style-label">Contact Info Icons</label>
              <button className={`sep-btn ${styles.showIcons ? 'active' : ''}`} onClick={() => updateStyle('showIcons', !styles.showIcons)}>Show Icons</button>
            </div>
            {styles.showIcons && (
              <div className="separator-toggle row" style={{ marginTop: -2 }}>
                <button className={`sep-btn ${(styles.iconColor ?? 'default') === 'default' ? 'active' : ''}`} onClick={() => updateStyle('iconColor', 'default')}>Default Colors</button>
                <button className={`sep-btn ${styles.iconColor === 'accent' ? 'active' : ''}`} onClick={() => updateStyle('iconColor', 'accent')}>Theme Colors</button>
              </div>
            )}
            <button className="btn-reset-margins" onClick={reset}>↺ Reset to defaults</button>
          </div>
        )}
      </div>

      {showPhoto && (
        <div className="photo-upload-row">
          <span className="field-label">Headshot Photo</span>
          <div className="photo-upload-controls">
            {data.photo && <img src={data.photo} alt="Headshot" className="photo-preview-thumb" />}
            <button className="btn-photo-upload" onClick={() => photoRef.current.click()}>{data.photo ? '↺ Change Photo' : '+ Upload Photo'}</button>
            {data.photo && <button className="btn-photo-remove" onClick={() => update('photo', null)}>✕ Remove</button>}
            <input ref={photoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoChange} />
          </div>
        </div>
      )}

      <Field label="Full Name" value={data.name} onChange={v => update('name', v)} placeholder="John Smith" />
      <Field label="Professional Title" value={data.title} onChange={v => update('title', v)} placeholder="Senior Software Engineer" />
      <div className="field-row">
        <Field label="Email" value={data.email} onChange={v => update('email', v)} placeholder="john@email.com" type="email" />
        <Field label="Phone" value={data.phone} onChange={v => update('phone', v)} placeholder="+1 555 000 0000" />
      </div>
      <Field label="Location" value={data.location} onChange={v => update('location', v)} placeholder="Boston, MA" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="LinkedIn URL" value={data.linkedin} onChange={v => update('linkedin', v)} placeholder="linkedin.com/in/john" />
          <Field
            label="LinkedIn label"
            value={data.linkedinLabel || ''}
            onChange={v => update('linkedinLabel', v)}
            placeholder="LinkedIn"
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Website" value={data.website} onChange={v => update('website', v)} placeholder="johnsmith.dev" />
          <Field
            label="Website label"
            value={data.websiteLabel || ''}
            onChange={v => update('websiteLabel', v)}
            placeholder="Portfolio"
          />
        </div>
      </div>
      <div className="ai-field-wrapper">
        <div className="ai-field-label-row">
          <span className="field-label">Professional Summary</span>
          <button className="btn-ai-suggest" onClick={handleSuggestSummary} disabled={suggestingSum}>
            {suggestingSum ? '⏳ Writing…' : data.summary?.trim() ? '✨ Improve' : '✨ Suggest'}
          </button>
        </div>
        <textarea className="field-input" value={data.summary} onChange={e => update('summary', e.target.value)} placeholder="A brief 2–3 sentence overview of your professional background and goals…" rows={4} />
        {suggestedSummary && (
          <div className="ai-preview">
            <div className="ai-preview-label">✨ AI Suggestion</div>
            <p className="ai-preview-text">{suggestedSummary}</p>
            <div className="ai-preview-actions">
              <button className="btn-ai-accept" onClick={acceptSummary}>Apply</button>
              <button className="btn-ai-discard" onClick={discardSummary}>Discard</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Editable value display for sliders ────────────────────
function EditableValue({ value, min, max, step, unit = '', decimals = 2, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState('');

  const roundToStep = (v) => {
    const rounded = Math.round(v / step) * step;
    const clamped = Math.min(max, Math.max(min, rounded));
    return parseFloat(clamped.toFixed(10));
  };

  const commit = () => {
    const parsed = parseFloat(draft);
    if (!isNaN(parsed)) onChange(roundToStep(parsed));
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') commit();
    if (e.key === 'Escape') setEditing(false);
  };

  if (editing) {
    return (
      <input className="editable-value-input" autoFocus value={draft}
        onChange={e => setDraft(e.target.value)} onBlur={commit} onKeyDown={handleKeyDown}
        style={{ width: `${Math.max(36, (draft.length + 1) * 8)}px` }}
      />
    );
  }

  return (
    <span className="style-value editable-value" onClick={() => { setDraft(value.toFixed(decimals)); setEditing(true); }} title="Click to enter a value">
      {value.toFixed(decimals)}{unit}
    </span>
  );
}

// ── Style Toolbar ─────────────────────────────────────────
const FONT_OPTIONS = [
  { label: 'DM Sans',      value: 'DM Sans' },
  { label: 'Georgia',      value: 'Georgia' },
  { label: 'Times New Roman', value: 'Times New Roman' },
  { label: 'Helvetica',    value: 'Helvetica, Arial, sans-serif' },
  { label: 'Garamond',     value: 'Garamond, serif' },
  { label: 'Courier New',  value: 'Courier New, monospace' },
];

function StyleToolbar({ styles, updateStyle, showBulletSpacing = false, showRoleOrder = false, defaultStyles, onRoleOrderChange, batchStyle }) {
  const [open, setOpen] = useState(false);

  const reset = () => {
    if (batchStyle) {
      const changes = { fontSize: defaultStyles?.fontSize ?? 13 };
      if (showBulletSpacing) changes.bulletSpacing = defaultStyles?.bulletSpacing ?? 3;
      if (showRoleOrder) changes.roleOrder = defaultStyles?.roleOrder ?? 'company-first';
      if (styles.formatting) changes.formatting = defaultStyles?.formatting ?? styles.formatting;
      batchStyle(changes);
    } else {
      updateStyle('fontSize', defaultStyles?.fontSize ?? 13);
      if (showBulletSpacing) updateStyle('bulletSpacing', defaultStyles?.bulletSpacing ?? 3);
      if (showRoleOrder) updateStyle('roleOrder', defaultStyles?.roleOrder ?? 'company-first');
      if (styles.formatting) updateStyle('formatting', defaultStyles?.formatting ?? styles.formatting);
    }
  };

  return (
    <div className="style-toolbar">
      <button className="style-toolbar-toggle" onClick={() => setOpen(o => !o)}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/>
        </svg>
        Style Options
        <span className="style-toolbar-chevron">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="style-toolbar-body">
          <div className="style-row">
            <label className="style-label">Body Text <EditableValue value={styles.fontSize} min={10} max={16} step={0.5} unit="px" decimals={1} onChange={v => updateStyle('fontSize', v)} /></label>
            <input type="range" min={10} max={16} step={0.5} value={styles.fontSize} onChange={e => updateStyle('fontSize', parseFloat(e.target.value))} className="style-slider" />
          </div>
          {showBulletSpacing && (
            <div className="style-row">
              <label className="style-label">Bullet Spacing <EditableValue value={styles.bulletSpacing} min={0} max={12} step={1} unit="px" decimals={0} onChange={v => updateStyle('bulletSpacing', v)} /></label>
              <input type="range" min={0} max={12} step={1} value={styles.bulletSpacing} onChange={e => updateStyle('bulletSpacing', parseInt(e.target.value))} className="style-slider" />
            </div>
          )}
          {showRoleOrder && (
            <div className="style-row">
              <label className="style-label">Company / Job Title Order</label>
              <div className="separator-toggle row">
                {[['company-first', 'Company First'], ['role-first', 'Job Title First']].map(([val, label]) => (
                  <button
                    key={val}
                    className={`sep-btn ${(styles.roleOrder ?? 'company-first') === val ? 'active' : ''}`}
                    onClick={() => { updateStyle('roleOrder', val); onRoleOrderChange && onRoleOrderChange(); }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <button className="btn-reset-margins" onClick={reset}>↺ Reset to defaults</button>
        </div>
      )}
    </div>
  );
}

// ── Experience ────────────────────────────────────────────
function ExperienceForm({ items, add, update, remove, removeBullet, addSubRole, updateSubRole, removeSubRole, styles, updateStyle, defaultStyles, batchSectionStyle, applyAIBullets, updateExperienceWithHistory }) {
  return (
    <div className="form-section">
      <h2 className="section-heading">Work Experience</h2>
      <StyleToolbar styles={styles} updateStyle={updateStyle} showBulletSpacing={true} showRoleOrder={true} defaultStyles={defaultStyles} batchStyle={batchSectionStyle ? (changes) => batchSectionStyle('experience', changes) : null}
        onRoleOrderChange={() => {
          // Clear per-entry bold overrides so position takes over cleanly
          items.forEach(exp => {
            const fmt = exp.formatting;
            if (!fmt) return;
            const newFmt = { ...fmt };
            if (newFmt.company) { const { bold: _bc, ...rest } = newFmt.company; newFmt.company = rest; }
            if (newFmt.role)    { const { bold: _br, ...rest } = newFmt.role;    newFmt.role    = rest; }
            update(exp.id, 'formatting', newFmt);
          });
        }}
      />
      {items.length === 0 && (
        <p className="empty-hint">No experience added yet. Click the button below to add your first role.</p>
      )}
      {items.map((exp, idx) => (
        <ExperienceCard
          key={exp.id} exp={exp} idx={idx}
          update={update} remove={remove} removeBullet={removeBullet}
          addSubRole={addSubRole}
          updateSubRole={updateSubRole}
          removeSubRole={removeSubRole}
          styles={styles} updateStyle={updateStyle}
          applyAIBullets={applyAIBullets}
          updateExperienceWithHistory={updateExperienceWithHistory}
        />
      ))}
      <button className="btn-add" onClick={add}>
        <span>+</span> Add Experience
      </button>
    </div>
  );
}

function ExperienceCard({ exp, idx, update, remove, removeBullet, addSubRole, updateSubRole, removeSubRole, styles, updateStyle, applyAIBullets, updateExperienceWithHistory }) {
  const [open, setOpen] = useState(true);
  const [suggestingBullets, setSuggestingBullets] = useState(false);
  const [suggestedBullets, setSuggestedBullets]   = useState(null);
  const hasSubRoles = exp.subRoles && exp.subRoles.length > 0;

  // Per-entry formatting: reads/writes exp.formatting, not sectionStyles
  const roleOrder    = styles?.roleOrder ?? 'company-first';
  // Position default: top field = bold, bottom field = normal
  const positionBold = (field) => field === (roleOrder === 'company-first' ? 'company' : 'role');

  const toggleFmt = (field, prop) => {
    const current  = exp.formatting?.[field] ?? {};
    const newField = { ...current };
    if (prop === 'bold') {
      if (newField.bold !== undefined) {
        // Has explicit override — remove it, go back to position default
        delete newField.bold;
      } else {
        // Using position default — store the opposite to override it
        // Top field (bold by position): store false to un-bold
        // Bottom field (normal by position): store true to bold
        newField.bold = !positionBold(field);
      }
    } else {
      newField[prop] = !current[prop];
    }
    updateExperienceWithHistory(exp.id, 'formatting', { ...exp.formatting, [field]: newField });
  };

  const isEffectivelyActive = (field, prop) => {
    const explicit = exp.formatting?.[field]?.[prop];
    if (prop !== 'bold') return !!explicit;
    if (explicit === true)  return true;
    if (explicit === false) return false;
    // No explicit override — use position default
    return positionBold(field);
  };

  const FmtBtns = ({ field }) => (
    <div className="fmt-toggle inline">
      {['bold', 'italic', 'underline'].map(prop => (
        <button key={prop} className={`fmt-btn ${isEffectivelyActive(field, prop) ? 'active' : ''}`} onClick={() => toggleFmt(field, prop)} title={prop}>
          {prop === 'bold' && <b>B</b>}
          {prop === 'italic' && <i>I</i>}
          {prop === 'underline' && <u>U</u>}
        </button>
      ))}
    </div>
  );

  const updateBullet = (i, val) => {
    const bullets = [...exp.bullets]; bullets[i] = val;
    update(exp.id, 'bullets', bullets);
  };
  const addBullet    = () => update(exp.id, 'bullets', [...exp.bullets, '']);
  const doRemoveBullet = removeBullet ? (i) => removeBullet(exp.id, i) : (i) => update(exp.id, 'bullets', exp.bullets.filter((_, j) => j !== i));

  const bulletDragIdx = React.useRef(null);
  const [bulletInsertAt, setBulletInsertAt] = useState(null);
  const reorderBullets = (from, to) => {
    const bullets = [...exp.bullets];
    const [moved] = bullets.splice(from, 1);
    bullets.splice(to, 0, moved);
    update(exp.id, 'bullets', bullets);
  };
  const onBulletDragStart = (i) => { bulletDragIdx.current = i; };
  const onBulletDragOver = (e, i) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const mid  = rect.top + rect.height / 2;
    setBulletInsertAt(e.clientY < mid ? i : i + 1);
  };
  const onBulletDragEnd = () => {
    if (bulletDragIdx.current !== null && bulletInsertAt !== null) {
      const from = bulletDragIdx.current;
      const to = bulletInsertAt > from ? bulletInsertAt - 1 : bulletInsertAt;
      if (from !== to) reorderBullets(from, to);
    }
    bulletDragIdx.current = null;
    setBulletInsertAt(null);
  };

  const handleSuggestBullets = async () => {
    setSuggestingBullets(true);
    try {
      const bullets = await suggestBullets({ role: exp.role, company: exp.company, existingBullets: exp.bullets });
      setSuggestedBullets(bullets);
    } catch (err) {
      alert('AI suggestion failed: ' + err.message);
    } finally {
      setSuggestingBullets(false);
    }
  };

  const acceptOneBullet = (suggestion) => {
    const existing = [...exp.bullets];
    if (suggestion.index < existing.length) existing[suggestion.index] = suggestion.text;
    else existing.push(suggestion.text);
    applyAIBullets(exp.id, existing);
    setSuggestedBullets(prev => {
      const remaining = prev.filter(s => s.index !== suggestion.index);
      return remaining.length ? remaining : null;
    });
  };

  const acceptAllBullets = () => { applyAIBullets(exp.id, suggestedBullets.map(s => s.text)); setSuggestedBullets(null); };
  const discardBullets   = () => setSuggestedBullets(null);

  return (
    <div className="card">
      <div className="card-header" onClick={() => setOpen(o => !o)}>
        <div className="card-title">
          <span className="card-index">#{idx + 1}</span>
          <span>{exp.company || exp.role || 'New Position'}</span>
        </div>
        <div className="card-controls">
          <button className="btn-icon" onClick={e => { e.stopPropagation(); remove(exp.id); }} title="Remove">✕</button>
          <span className="card-chevron">{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {open && (
        <div className="card-body">
          {/* Company field */}
          <div className="field-with-fmt">
            <div className="field-label-row-fmt">
              <span className="field-label">Company</span>
              <FmtBtns field="company" />
            </div>
            <input className="field-input" value={exp.company} onChange={e => update(exp.id, 'company', e.target.value)} placeholder="Acme Corp" />
          </div>

          {/* Main role (hidden label if sub-roles exist) */}
          {!hasSubRoles && (
            <div className="field-with-fmt">
              <div className="field-label-row-fmt">
                <span className="field-label">Job Title</span>
                <FmtBtns field="role" />
              </div>
              <input className="field-input" value={exp.role} onChange={e => update(exp.id, 'role', e.target.value)} placeholder="Product Manager" />
            </div>
          )}

          {/* Dates (only shown when no sub-roles) */}
          {!hasSubRoles && (
            <>
              <div className="field-group-with-fmt">
                <div className="field-label-row-fmt">
                  <span className="field-label">Dates</span>
                  <FmtBtns field="date" />
                </div>
                <div className="field-row">
                  <input className="field-input" value={exp.startDate} onChange={e => update(exp.id, 'startDate', e.target.value)} placeholder="Jan 2022" />
                  <input className="field-input" value={exp.endDate} onChange={e => update(exp.id, 'endDate', e.target.value)} placeholder="Present" disabled={exp.current} />
                </div>
              </div>
              <label className="checkbox-label">
                <input type="checkbox" checked={exp.current} onChange={e => update(exp.id, 'current', e.target.checked)} />
                I currently work here
              </label>
            </>
          )}

          {/* Bullets (hidden when sub-roles exist) */}
          {!hasSubRoles && (
            <>
              <div className="ai-field-label-row">
                <div className="bullets-label">Key Accomplishments</div>
                <button className="btn-ai-suggest" onClick={handleSuggestBullets} disabled={suggestingBullets}>
                  {suggestingBullets ? '⏳ Writing…' : exp.bullets.filter(b => b.trim()).length ? '✨ Improve' : '✨ Suggest'}
                </button>
              </div>
              {exp.bullets.map((b, i) => (
                <React.Fragment key={i}>
                  {bulletInsertAt === i && <div className="bullet-drop-indicator" />}
                  <div className="bullet-row" onDragOver={(e) => onBulletDragOver(e, i)} style={{ opacity: bulletDragIdx.current === i ? 0.35 : 1 }}>
                    <span className="bullet-drag-handle" draggable onDragStart={() => onBulletDragStart(i)} onDragEnd={onBulletDragEnd}>⠿</span>
                    <textarea className="bullet-input" value={b} onChange={e => updateBullet(i, e.target.value)} placeholder="Achieved X by doing Y, resulting in Z…" rows={2} />
                    {exp.bullets.length > 1 && <button className="btn-icon small" onClick={() => doRemoveBullet(i)}>✕</button>}
                  </div>
                </React.Fragment>
              ))}
              {bulletInsertAt === exp.bullets.length && <div className="bullet-drop-indicator" />}
              <button className="btn-add-bullet" onClick={addBullet}>+ Add bullet</button>
              {suggestedBullets && (
                <div className="ai-preview">
                  <div className="ai-preview-label-row">
                    <span className="ai-preview-label">✨ AI Suggestions</span>
                    <div className="ai-preview-actions">
                      <button className="btn-ai-accept" onClick={acceptAllBullets}>Apply All</button>
                      <button className="btn-ai-discard" onClick={discardBullets}>Discard</button>
                    </div>
                  </div>
                  {suggestedBullets.map((s) => {
                    const isReplace = s.index < exp.bullets.filter(b => b.trim()).length;
                    return (
                      <div className="ai-bullet-row" key={s.index}>
                        <div className="ai-bullet-content">
                          <span className={`ai-bullet-tag ${isReplace ? 'tag-replace' : 'tag-add'}`}>{isReplace ? `Replace #${s.index + 1}` : 'Add'}</span>
                          <span className="ai-bullet-text">• {s.text}</span>
                        </div>
                        <button className="btn-ai-accept-one" onClick={() => acceptOneBullet(s)}>Apply</button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* ── Sub-roles ── */}
          {hasSubRoles && (
            <div style={{ marginTop: 4 }}>
              <div className="bullets-label" style={{ marginBottom: 8 }}>Roles at this Company</div>
              {exp.subRoles.map((sr, srIdx) => (
                <SubRoleCard
                  key={sr.id}
                  sr={sr}
                  srIdx={srIdx}
                  expId={exp.id}
                  updateSubRole={updateSubRole}
                  removeSubRole={removeSubRole}
                  fmtBtns={<FmtBtns field="role" />}
                />
              ))}
            </div>
          )}

          {/* Add sub-role button */}
          <button
            className="btn-add"
            style={{ marginTop: 10, fontSize: 13 }}
            onClick={() => addSubRole(exp.id)}
          >
            <span>+</span> Add Another Role at this Company
          </button>
        </div>
      )}
    </div>
  );
}

// ── Sub-role card ─────────────────────────────────────────
function SubRoleCard({ sr, srIdx, expId, updateSubRole, removeSubRole, fmtBtns }) {
  const [open, setOpen] = useState(true);

  const updateBullet = (i, val) => {
    const bullets = [...(sr.bullets || [''])];
    bullets[i] = val;
    updateSubRole(expId, sr.id, 'bullets', bullets);
  };
  const addBullet    = () => updateSubRole(expId, sr.id, 'bullets', [...(sr.bullets || ['']), '']);
  const removeBullet = (i) => updateSubRole(expId, sr.id, 'bullets', (sr.bullets || ['']).filter((_, j) => j !== i));

  const bulletDragIdx = React.useRef(null);
  const [bulletInsertAt, setBulletInsertAt] = useState(null);
  const reorderBullets = (from, to) => {
    const bullets = [...(sr.bullets || [''])];
    const [moved] = bullets.splice(from, 1);
    bullets.splice(to, 0, moved);
    updateSubRole(expId, sr.id, 'bullets', bullets);
  };
  const onBulletDragStart = (i) => { bulletDragIdx.current = i; };
  const onBulletDragOver = (e, i) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const mid  = rect.top + rect.height / 2;
    setBulletInsertAt(e.clientY < mid ? i : i + 1);
  };
  const onBulletDragEnd = () => {
    if (bulletDragIdx.current !== null && bulletInsertAt !== null) {
      const from = bulletDragIdx.current;
      const to = bulletInsertAt > from ? bulletInsertAt - 1 : bulletInsertAt;
      if (from !== to) reorderBullets(from, to);
    }
    bulletDragIdx.current = null;
    setBulletInsertAt(null);
  };

  return (
    <div style={{ border: '1.5px solid var(--border)', borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--cream)', cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 13, color: 'var(--ink)' }}>
          <span style={{ background: 'var(--teal)', color: 'white', borderRadius: 4, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>#{srIdx + 1}</span>
          <span>{sr.role || 'New Role'}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn-icon" onClick={e => { e.stopPropagation(); removeSubRole(expId, sr.id); }} title="Remove role">✕</button>
          <span style={{ fontSize: 10, color: 'var(--ink-faint)' }}>{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {open && (
        <div className="card-body">
          <div className="field-with-fmt">
            <div className="field-label-row-fmt">
              <span className="field-label">Job Title</span>
              {fmtBtns}
            </div>
            <input className="field-input" value={sr.role} onChange={e => updateSubRole(expId, sr.id, 'role', e.target.value)} placeholder="Senior Engineer" />
          </div>
          <div className="field-row">
            <input className="field-input" value={sr.startDate || ''} onChange={e => updateSubRole(expId, sr.id, 'startDate', e.target.value)} placeholder="Jan 2022" />
            <input className="field-input" value={sr.endDate || ''} onChange={e => updateSubRole(expId, sr.id, 'endDate', e.target.value)} placeholder="Present" disabled={sr.current} />
          </div>
          <label className="checkbox-label">
            <input type="checkbox" checked={sr.current || false} onChange={e => updateSubRole(expId, sr.id, 'current', e.target.checked)} />
            I currently hold this role
          </label>
          <div className="bullets-label" style={{ marginTop: 8 }}>Key Accomplishments</div>
          {(sr.bullets || ['']).map((b, i) => (
            <React.Fragment key={i}>
              {bulletInsertAt === i && <div className="bullet-drop-indicator" />}
              <div className="bullet-row" onDragOver={(e) => onBulletDragOver(e, i)} style={{ opacity: bulletDragIdx.current === i ? 0.35 : 1 }}>
                <span className="bullet-drag-handle" draggable onDragStart={() => onBulletDragStart(i)} onDragEnd={onBulletDragEnd}>⠿</span>
                <textarea className="bullet-input" value={b} onChange={e => updateBullet(i, e.target.value)} placeholder="Achieved X by doing Y, resulting in Z…" rows={2} />
                {(sr.bullets || ['']).length > 1 && <button className="btn-icon small" onClick={() => removeBullet(i)}>✕</button>}
              </div>
            </React.Fragment>
          ))}
          {bulletInsertAt === (sr.bullets || ['']).length && <div className="bullet-drop-indicator" />}
          <button className="btn-add-bullet" onClick={addBullet}>+ Add bullet</button>
        </div>
      )}
    </div>
  );
}

// ── Education ─────────────────────────────────────────────
function EducationForm({ items, add, update, remove, styles, updateStyle, defaultStyles, batchSectionStyle }) {
  const toggleFmt = (field, prop) => {
    const current = styles?.formatting?.[field] ?? {};
    updateStyle('formatting', { ...styles?.formatting, [field]: { ...current, [prop]: !current[prop] } });
  };

  const FmtBtns = ({ field }) => (
    <div className="fmt-toggle inline">
      {['bold', 'italic', 'underline'].map(prop => (
        <button key={prop} className={`fmt-btn ${styles?.formatting?.[field]?.[prop] ? 'active' : ''}`} onClick={() => toggleFmt(field, prop)} title={prop}>
          {prop === 'bold' && <b>B</b>}
          {prop === 'italic' && <i>I</i>}
          {prop === 'underline' && <u>U</u>}
        </button>
      ))}
    </div>
  );

  return (
    <div className="form-section">
      <h2 className="section-heading">Education</h2>
      <StyleToolbar styles={styles} updateStyle={updateStyle} showBulletSpacing={false} defaultStyles={defaultStyles} batchStyle={batchSectionStyle ? (changes) => batchSectionStyle('education', changes) : null} />
      {items.length === 0 && <p className="empty-hint">No education added yet.</p>}
      {items.map((edu, idx) => (
        <div className="card" key={edu.id}>
          <div className="card-header">
            <div className="card-title">
              <span className="card-index">#{idx + 1}</span>
              <span>{edu.school || 'New School'}</span>
            </div>
            <button className="btn-icon" onClick={() => remove(edu.id)}>✕</button>
          </div>
          <div className="card-body">
            <div className="field-group-with-fmt">
              <div className="field-label-row-fmt">
                <span className="field-label">Degree &amp; Field of Study</span>
                <FmtBtns field="degree" />
              </div>
              <div className="field-row">
                <input className="field-input" value={edu.degree} onChange={e => update(edu.id, 'degree', e.target.value)} placeholder="B.S." />
                <input className="field-input" value={edu.field} onChange={e => update(edu.id, 'field', e.target.value)} placeholder="Computer Science" />
              </div>
            </div>
            <div className="field-with-fmt">
              <div className="field-label-row-fmt">
                <span className="field-label">School / University</span>
                <FmtBtns field="school" />
              </div>
              <input className="field-input" value={edu.school} onChange={e => update(edu.id, 'school', e.target.value)} placeholder="MIT" />
            </div>
            <div className="field-group-with-fmt">
              <div className="field-label-row-fmt">
                <span className="field-label">Dates</span>
                <FmtBtns field="date" />
              </div>
              <div className="field-row">
                <input className="field-input" value={edu.startDate} onChange={e => update(edu.id, 'startDate', e.target.value)} placeholder="Sept 2020" />
                <input className="field-input" value={edu.endDate} onChange={e => update(edu.id, 'endDate', e.target.value)} placeholder="May 2024" />
                <Field label="GPA" value={edu.gpa} onChange={v => update(edu.id, 'gpa', v)} placeholder="3.8" />
              </div>
            </div>
          </div>
        </div>
      ))}
      <button className="btn-add" onClick={add}><span>+</span> Add Education</button>
    </div>
  );
}

// ── Skills ────────────────────────────────────────────────
function SkillsForm({ skills, add, remove, reorder, styles, updateStyle, defaultStyles, templateFeatures = {}, suggestions = [], suggestionsLoading = false, batchSectionStyle }) {
  const [input,      setInput]      = useState('');
  const [styleOpen,  setStyleOpen]  = useState(false);
  const [insertAt,   setInsertAt]   = useState(null); // gap index to show insertion line
  const dragIdx = React.useRef(null);

  const MAX_SKILL_LEN = 30;
  const handleAdd = () => { const t = input.trim().slice(0, MAX_SKILL_LEN); if (t) { add(t); setInput(''); } };
  const handleKeyDown = (e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); handleAdd(); } };
  const toggleFmt = (prop) => updateStyle('formatting', { ...styles?.formatting, [prop]: !styles?.formatting?.[prop] });

  const onDragStart = (i) => { dragIdx.current = i; };
  // Determine insertion gap from mouse position relative to the tag
  const onDragOver = (e, i) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const mid  = rect.left + rect.width / 2;
    setInsertAt(e.clientX < mid ? i : i + 1);
  };
  const onDragEnd = () => {
    if (dragIdx.current !== null && insertAt !== null) {
      const from = dragIdx.current;
      // Adjust target index accounting for removal of dragged item
      const to = insertAt > from ? insertAt - 1 : insertAt;
      if (from !== to) reorder(from, to);
    }
    dragIdx.current = null;
    setInsertAt(null);
  };

  // Filter out skills already added (case-insensitive)
  const skillsLower = skills.map(s => s.toLowerCase());
  const visibleSuggestions = suggestions.filter(s => !skillsLower.includes(s.toLowerCase()));

  return (
    <div className="form-section">
      <h2 className="section-heading">Skills</h2>
      <div className="style-toolbar">
        <button className="style-toolbar-toggle" onClick={() => setStyleOpen(o => !o)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/>
          </svg>
          Style Options
          <span className="style-toolbar-chevron">{styleOpen ? '▲' : '▼'}</span>
        </button>
        {styleOpen && (
          <div className="style-toolbar-body">
            {templateFeatures.skillsSeparator !== false && (
              <div className="style-row">
                <label className="style-label">Separator Style</label>
                <div className="separator-toggle row">
                  <button className={`sep-btn ${styles.separator === 'comma' ? 'active' : ''}`} onClick={() => updateStyle('separator', 'comma')}>Comma Separated</button>
                  <button className={`sep-btn ${styles.separator === 'marker' ? 'active' : ''}`} onClick={() => updateStyle('separator', 'marker')}>Marker Separated</button>
                </div>
              </div>
            )}
            <button className="btn-reset-margins" onClick={() => {
              if (batchSectionStyle) {
                batchSectionStyle('skills', { separator: defaultStyles?.separator ?? 'comma', formatting: { bold: false, italic: false, underline: false } });
              } else {
                updateStyle('separator', defaultStyles?.separator ?? 'comma');
                updateStyle('formatting', { bold: false, italic: false, underline: false });
              }
            }}>
              ↺ Reset to defaults
            </button>
          </div>
        )}
      </div>
      <div className="skills-input-row">
        <input className="field-input" value={input} onChange={e => setInput(e.target.value.slice(0, MAX_SKILL_LEN))} onKeyDown={handleKeyDown} placeholder="Type a skill and press Enter…" maxLength={MAX_SKILL_LEN} />
        <button className="btn-add-skill" onClick={handleAdd}>Add</button>
      </div>
      <div className="skills-fmt-row">
        <span className="field-label">Skill Formatting</span>
        <div className="fmt-toggle inline">
          {['bold', 'italic', 'underline'].map(prop => (
            <button key={prop} className={`fmt-btn ${styles?.formatting?.[prop] ? 'active' : ''}`} onClick={() => toggleFmt(prop)} title={prop}>
              {prop === 'bold' && <b>B</b>}
              {prop === 'italic' && <i>I</i>}
              {prop === 'underline' && <u>U</u>}
            </button>
          ))}
        </div>
      </div>
      <div className="skill-tags">
        {skills.map((s, i) => (
          <React.Fragment key={s}>
            {insertAt === i && <span className="skill-drop-indicator" />}
            <span
              className="skill-tag"
              onDragOver={(e) => onDragOver(e, i)}
              style={{
                cursor: 'default',
                opacity: dragIdx.current === i ? 0.35 : 1,
                fontWeight: styles?.formatting?.bold ? 'bold' : undefined,
                fontStyle: styles?.formatting?.italic ? 'italic' : undefined,
                textDecoration: styles?.formatting?.underline ? 'underline' : undefined,
              }}
            >
              <span
                draggable
                onDragStart={() => onDragStart(i)}
                onDragEnd={onDragEnd}
                style={{ marginRight: 4, opacity: 0.4, fontSize: 11, cursor: 'grab' }}
              >⠿</span>
              {s} <button onClick={() => remove(s)}>✕</button>
            </span>
          </React.Fragment>
        ))}
        {insertAt === skills.length && <span className="skill-drop-indicator" />}
        {skills.length === 0 && <span className="empty-hint">No skills added yet.</span>}
      </div>
      {suggestionsLoading && (
        <div className="suggestions-label" style={{ color: 'var(--ink-faint)' }}>⏳ Finding suggestions…</div>
      )}
      {!suggestionsLoading && visibleSuggestions.length > 0 && (
        <>
          <div className="suggestions-label">✨ Suggested for you:</div>
          <div className="suggestions">
            {visibleSuggestions.map(s => (
              <button key={s} className="suggestion-btn" onClick={() => add(s)}>{s}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Misc ──────────────────────────────────────────────────
function MiscForm({ sections, addSection, updateSection, removeSection, addEntry, updateEntry, removeEntry, styles, updateStyle, defaultStyles, updateMiscCiteFmt }) {
  return (
    <div className="form-section">
      <h2 className="section-heading">Miscellaneous</h2>
      <p style={{ fontSize: 13, color: 'var(--ink-faint)', marginBottom: 16 }}>
        Add custom sections like Publications, Certifications, Awards, or anything else.
      </p>
      {sections.map((sec, si) => (
        <MiscSectionCard
          key={sec.id}
          sec={sec}
          si={si}
          updateSection={updateSection}
          removeSection={removeSection}
          addEntry={addEntry}
          updateEntry={updateEntry}
          removeEntry={removeEntry}
          updateCiteFmt={updateMiscCiteFmt}
        />
      ))}
      <button className="btn-add" onClick={addSection}>
        <span>+</span> Add Section
      </button>
    </div>
  );
}

function MiscSectionCard({ sec, si, updateSection, removeSection, addEntry, updateEntry, removeEntry, updateCiteFmt }) {
  const [open, setOpen] = useState(true);
  // Derive citeFmt from sec prop so undo correctly updates the button highlight
  const citeFmt = sec.citeFmt || 'apa';
  const isPubSection = /publication|journal|conference|paper/i.test(sec.title || '');

  const reformatCitations = (fmt) => {
    if (updateCiteFmt) updateCiteFmt(sec.id, fmt);
    else updateSection(sec.id, 'citeFmt', fmt);
  };

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-header" onClick={() => setOpen(o => !o)}>
        <div className="card-title">
          <span className="card-index">#{si + 1}</span>
          <span style={{ color: sec.title ? 'var(--ink)' : 'var(--ink-faint)', fontStyle: sec.title ? 'normal' : 'italic' }}>{sec.title || 'Untitled Section'}</span>
        </div>
        <div className="card-controls">
          <button className="btn-icon" onClick={e => { e.stopPropagation(); removeSection(sec.id); }} title="Remove section">✕</button>
          <span className="card-chevron">{open ? '▲' : '▼'}</span>
        </div>
      </div>
      {open && (
        <div className="card-body">
          <div className="field">
            <label className="field-label">Section Title</label>
            <input
              className="field-input"
              value={sec.title}
              onChange={e => updateSection(sec.id, 'title', e.target.value)}
              placeholder="Publications, Certifications, Awards…"
            />
          </div>
          {isPubSection && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, marginBottom: 2 }}>
              <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--ink-faint)' }}>Citation format</span>
              {['apa', 'mla', 'ieee'].map(fmt => (
                <button
                  key={fmt}
                  onClick={() => reformatCitations(fmt)}
                  style={{
                    padding: '3px 11px', borderRadius: 6, border: '1.5px solid',
                    borderColor: citeFmt === fmt ? 'var(--teal)' : 'var(--border)',
                    background: citeFmt === fmt ? 'var(--teal)' : 'transparent',
                    color: citeFmt === fmt ? '#fff' : 'var(--ink-light)',
                    fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-body)', cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {fmt.toUpperCase()}
                </button>
              ))}
            </div>
          )}
          <div className="bullets-label" style={{ marginTop: 12, marginBottom: 8 }}>Entries</div>
          {(sec.entries || []).map((entry, ei) => (
            <MiscEntryCard
              key={entry.id}
              entry={entry}
              ei={ei}
              secId={sec.id}
              total={sec.entries.length}
              updateEntry={updateEntry}
              removeEntry={removeEntry}
            />
          ))}
          <button className="btn-add-bullet" style={{ marginTop: 4 }} onClick={() => addEntry(sec.id)}>
            + Add entry
          </button>
        </div>
      )}
    </div>
  );
}

function MiscEntryCard({ entry, ei, secId, total, updateEntry, removeEntry }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ border: '1.5px solid var(--border)', borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--cream)', cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}
      >
        <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)' }}>
          {entry.title || `Entry ${ei + 1}`}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {total > 1 && (
            <button className="btn-icon" onClick={e => { e.stopPropagation(); removeEntry(secId, entry.id); }} title="Remove entry">✕</button>
          )}
          <span style={{ fontSize: 10, color: 'var(--ink-faint)' }}>{open ? '▲' : '▼'}</span>
        </div>
      </div>
      {open && (
        <div className="card-body">
          <div className="field-row">
            <div className="field">
              <label className="field-label">Title</label>
              <input className="field-input" value={entry.title} onChange={e => updateEntry(secId, entry.id, 'title', e.target.value)} placeholder="Paper title, Certificate name…" />
            </div>
            <div className="field">
              <label className="field-label">Date</label>
              <input className="field-input" value={entry.date} onChange={e => updateEntry(secId, entry.id, 'date', e.target.value)} placeholder="2023" />
            </div>
          </div>
          <div className="field">
            <label className="field-label">Subtitle / Issuer</label>
            <input className="field-input" value={entry.subtitle} onChange={e => updateEntry(secId, entry.id, 'subtitle', e.target.value)} placeholder="Journal name, Issuing organization…" />
          </div>
          <div className="field">
            <label className="field-label">Description (optional)</label>
            <textarea className="field-input" rows={2} value={entry.description} onChange={e => updateEntry(secId, entry.id, 'description', e.target.value)} placeholder="Brief description…" />
          </div>
        </div>
      )}
    </div>
  );
}


// ── Section Order Panel ────────────────────────────────
const SECTION_LABELS = { experience: 'Experience', education: 'Education', skills: 'Skills' };

function SectionOrderPanel({ settings, resume, reorderSections, toggleSectionVisibility }) {
  const dragIdx    = React.useRef(null);
  const localOrder = React.useRef(null);
  const [dragging, setDragging] = React.useState(null);

  // Build full ordered list including misc sections
  const baseSections = settings.sectionOrder ?? ['experience', 'education', 'skills'];
  const miscIds = (resume?.misc ?? []).filter(s => s.title).map(s => 'misc:' + s.id);
  const newMisc = miscIds.filter(k => !baseSections.includes(k));
  const committedOrder = [...baseSections.filter(k => {
    if (k === 'experience' || k === 'education' || k === 'skills') return true;
    if (k.startsWith('misc:')) {
      const id = parseInt(k.split(':')[1]);
      return (resume?.misc ?? []).some(s => s.id === id);
    }
    return false;
  }), ...newMisc];
  // Use local drag order for visual feedback, fall back to committed order
  const order = localOrder.current ?? committedOrder;
  const hidden = settings.hiddenSections ?? [];

  const getLabel = (key) => {
    if (SECTION_LABELS[key]) return SECTION_LABELS[key];
    if (key.startsWith('misc:')) {
      const id = parseInt(key.split(':')[1]);
      const sec = (resume?.misc ?? []).find(s => s.id === id);
      return sec?.title || 'Untitled Section';
    }
    return key;
  };

  const hoverIdx = React.useRef(null);

  const getFullOrder = () => {
    const order   = settings.sectionOrder ?? ['experience', 'education', 'skills'];
    const miscKeys = (resume?.misc ?? []).filter(s => s.title).map(s => 'misc:' + s.id);
    const newMisc  = miscKeys.filter(k => !order.includes(k));
    return [...order.filter(k => {
      if (['experience','education','skills'].includes(k)) return true;
      if (k.startsWith('misc:')) { const id = parseInt(k.split(':')[1]); return (resume?.misc ?? []).some(s => s.id === id); }
      return false;
    }), ...newMisc];
  };

  const handleDragStart = (i) => {
    dragIdx.current = i;
    hoverIdx.current = i;
    localOrder.current = getFullOrder();
    setDragging(i);
  };
  const handleDragEnter = (i) => {
    if (dragIdx.current === null || !localOrder.current) return;
    if (hoverIdx.current === i) return;
    hoverIdx.current = i;
    const order = [...localOrder.current];
    const [moved] = order.splice(dragIdx.current, 1);
    order.splice(i, 0, moved);
    localOrder.current = order;
    dragIdx.current = i;
    setDragging(i);
  };
  const handleDragEnd = () => {
    if (localOrder.current) reorderSections(localOrder.current);
    dragIdx.current = null;
    hoverIdx.current = null;
    localOrder.current = null;
    setDragging(null);
  };

  return (
    <div className="field" style={{ marginBottom: 16 }}>
      <label className="field-label" style={{ marginBottom: 8, display: 'block' }}>Section Order</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {order.map((key, i) => {
          const isHidden = hidden.includes(key);
          return (
            <div
              key={key}
              onDragEnter={() => handleDragEnter(i)}
              onDragOver={e => e.preventDefault()}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 10px', borderRadius: 7,
                background: dragging === i ? 'var(--teal-light)' : 'var(--cream)',
                border: '1.5px solid var(--border)',
                opacity: isHidden ? 0.45 : 1,
                transition: 'background 0.15s, opacity 0.15s',
              }}
            >
              <span
                draggable
                onDragStart={() => handleDragStart(i)}
                onDragEnd={handleDragEnd}
                style={{ color: 'var(--ink-faint)', fontSize: 14, cursor: 'grab', flexShrink: 0, padding: '0 2px' }}
              >⠿</span>
              <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{getLabel(key)}</span>
              <button
                onClick={() => toggleSectionVisibility(key)}
                title={isHidden ? 'Show section' : 'Hide section'}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: isHidden ? 'var(--ink-faint)' : 'var(--teal)', flexShrink: 0 }}
              >
                {isHidden ? (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Page Setup ────────────────────────────────────────────
function PageSetupForm({ settings, update, updateLive, commitHistory, templateFeatures = {}, defaultPageSettings, densityPresets = [], applyDensityPreset, sectionStyles, selectedDensityLabel, batchPageSettings, reorderSections, toggleSectionVisibility, resume, onMasterReset, numPages, applyBulletSuggestion, applySummarySuggestion, removeSkill }) {
  const MARGIN_MIN  = 0.25;
  const MARGIN_MAX  = 2.0;
  const MARGIN_STEP = 0.25;
  const d = defaultPageSettings ?? settings;

  const reset = () => {
    if (batchPageSettings) {
      batchPageSettings({
        marginTop:    d.marginTop    ?? 1.0,
        marginBottom: d.marginBottom ?? 1.0,
        marginLeft:   d.marginLeft   ?? 1.0,
        marginRight:  d.marginRight  ?? 1.0,
        lineHeight:   d.lineHeight   ?? 1.6,
        colorScheme:  d.colorScheme  ?? 'teal',
        bodyFont:     d.bodyFont     ?? 'DM Sans',
      });
    } else {
      update('marginTop',    d.marginTop    ?? 1.0);
      update('marginBottom', d.marginBottom ?? 1.0);
      update('marginLeft',   d.marginLeft   ?? 1.0);
      update('marginRight',  d.marginRight  ?? 1.0);
      update('lineHeight',   d.lineHeight   ?? 1.6);
      update('colorScheme',  d.colorScheme  ?? 'teal');
      update('bodyFont',     d.bodyFont     ?? 'DM Sans');
    }
  };

  const SCHEMES = [
    { id: 'teal',     color: '#2a6b6b', label: 'Teal'     },
    { id: 'navy',     color: '#1e3a5f', label: 'Navy'     },
    { id: 'burgundy', color: '#7a2040', label: 'Burgundy' },
    { id: 'forest',   color: '#2d5a2d', label: 'Forest'   },
    { id: 'slate',    color: '#455a64', label: 'Slate'    },
    { id: 'coral',    color: '#c4522e', label: 'Coral'    },
    { id: 'none',     color: '#1a1a1a', label: 'None'     },
  ];

  const current = settings.colorScheme ?? 'teal';
  const customColorRef = React.useRef(null);
  const isCustom = current && !SCHEMES.find(s => s.id === current);
  const customColor = isCustom ? current : '#2a6b6b';

  // Highlight whichever preset label the user last selected
  const activeDensity = densityPresets.findIndex(p => p.label === selectedDensityLabel);
  // Track pre-drag snapshot for slider undo (only push history on mouseup, not on every drag tick)
  const preDragRef = React.useRef(null);
  const onSliderMouseDown = () => { preDragRef.current = { ...settings }; };
  const onSliderMouseUp   = () => { if (preDragRef.current && commitHistory) { commitHistory(preDragRef.current); preDragRef.current = null; } };

  // ── Fit to page ──
  const [fitTarget, setFitTarget]     = useState(null);
  const [fitLoading, setFitLoading]   = useState(false);
  const [fitAnalysis, setFitAnalysis] = useState(null);
  const [fitError, setFitError]       = useState(null);
  const [fitDismissed, setFitDismissed] = useState(new Set());
  const [fitOpen, setFitOpen]         = useState(false);

  const fitNorm = s => s.trim().toLowerCase().replace(/[\u2018\u2019\u201C\u201D]/g, "'").replace(/[\u2013\u2014]/g, '-').replace(/\s+/g, ' ');

  const handleFitToPage = async (target) => {
    setFitTarget(target);
    setFitLoading(true);
    setFitError(null);
    setFitAnalysis(null);
    setFitDismissed(new Set());
    try {
      const result = await suggestPageFit({ resume, currentPages: numPages, targetPages: target });
      setFitAnalysis(result);
    } catch (err) {
      setFitError(err.message);
    }
    setFitLoading(false);
  };

  // Derive applied state for fit suggestions (same pattern as Job Match)
  const fitBulletApplied = (improved) => {
    const allBullets = (resume.experience || []).flatMap(exp => [
      ...(exp.bullets || []),
      ...(exp.subRoles || []).flatMap(sr => sr.bullets || []),
    ]).map(b => fitNorm(b));
    return allBullets.includes(fitNorm(improved));
  };
  const fitSummaryApplied = fitAnalysis?.shortenedSummary &&
    fitNorm(resume.personal?.summary || '') === fitNorm(fitAnalysis.shortenedSummary);
  const fitSkillRemoved = (s) => !(resume.skills || []).includes(s);

  return (
    <div className="form-section">
      <h2 className="section-heading">Page Setup</h2>
      <p className="page-setup-desc">Adjust margins for your printed resume. The live preview will update in real time. All values are in inches.</p>

      {/* ── Fit to page dropdown ── */}
      {numPages > 1 && (
        <div className="style-toolbar fit-toolbar">
          <button className="style-toolbar-toggle" onClick={() => setFitOpen(o => !o)}>
            <span style={{ fontSize: 14 }}>📐</span>
            Page Trimming Suggestions
            <span className="style-toolbar-chevron">{fitOpen ? '▲' : '▼'}</span>
          </button>

          {fitOpen && (
            <div className="style-toolbar-body">

          {/* Step 1: Density presets */}
          {selectedDensityLabel !== 'Tight' && densityPresets.length > 0 && (
            <div className="fit-step">
              <div className="fit-step-label">Step 1 — Tighten layout</div>
              <div className="fit-card-btns">
                {densityPresets.filter(p => {
                  const order = ['Comfortable', 'Compact', 'Tight'];
                  return order.indexOf(p.label) > order.indexOf(selectedDensityLabel);
                }).map(p => (
                  <button
                    key={p.label}
                    className="fit-btn"
                    onClick={() => applyDensityPreset(p)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: AI content trimming */}
          <div className="fit-step">
            <div className="fit-step-label">
              {selectedDensityLabel === 'Tight' ? 'Suggest content to trim' : 'Step 2 — Trim content with AI'}
            </div>
            <div className="fit-card-btns">
              {numPages > 1 && (
                <button
                  className="fit-btn fit-btn-ai"
                  onClick={() => handleFitToPage(1)}
                  disabled={fitLoading}
                >
                  {fitLoading && fitTarget === 1 ? '⏳ Analyzing…' : '✨ Fit to 1 page'}
                </button>
              )}
              {numPages > 2 && (
                <button
                  className="fit-btn fit-btn-ai"
                  onClick={() => handleFitToPage(2)}
                  disabled={fitLoading}
                >
                  {fitLoading && fitTarget === 2 ? '⏳ Analyzing…' : '✨ Fit to 2 pages'}
                </button>
              )}
            </div>
          </div>

          {fitError && <div className="jm-error">{fitError}</div>}

          {fitAnalysis && (() => {
            const pendingBullets = (fitAnalysis.bulletChanges || []).filter(b => !fitBulletApplied(b.shortened) && !fitDismissed.has(b.original));
            const pendingSummary = fitAnalysis.shortenedSummary && !fitSummaryApplied && !fitDismissed.has('summary');
            const pendingSkills = (fitAnalysis.skillsToRemove || []).filter(s => !fitSkillRemoved(s));
            const hasPending = pendingBullets.length > 0 || pendingSummary || pendingSkills.length > 0;

            return hasPending ? (
              <div className="fit-results">
                <p className="fit-overview">{fitAnalysis.overview}</p>

                {/* Bullet shortenings — only unapplied and not dismissed */}
                {pendingBullets.length > 0 && (
                  <div className="fit-group">
                    <div className="jm-card-title">Shorten bullets</div>
                    {pendingBullets.map((b, i) => (
                      <div key={i} className="jm-bullet-item">
                        <div className="jm-bullet-current">
                          <span className="jm-bullet-label">Current</span>
                          <p>{b.original}</p>
                        </div>
                        <div className="jm-bullet-improved">
                          <span className="jm-bullet-label">Shorter</span>
                          <p>{b.shortened}</p>
                        </div>
                        {b.reason && <p className="jm-bullet-reason">{b.reason}</p>}
                        <div className="jm-item-actions">
                          <button className="jm-apply-btn" onClick={() => applyBulletSuggestion(b.original, b.shortened)}>Apply</button>
                          <button className="jm-discard-btn" onClick={() => setFitDismissed(prev => new Set([...prev, b.original]))}>Discard</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Summary shortening — hidden when applied or dismissed */}
                {pendingSummary && (
                  <div className="fit-group">
                    <div className="jm-card-title">Shorten summary</div>
                    <p className="jm-summary-text">{fitAnalysis.shortenedSummary}</p>
                    <div className="jm-item-actions">
                      <button className="jm-apply-btn" onClick={() => applySummarySuggestion(fitAnalysis.shortenedSummary)}>Apply</button>
                      <button className="jm-discard-btn" onClick={() => setFitDismissed(prev => new Set([...prev, 'summary']))}>Discard</button>
                    </div>
                  </div>
                )}

                {/* Skills to remove — only unapplied */}
                {pendingSkills.length > 0 && (
                  <div className="fit-group">
                    <div className="jm-card-title">Skills to remove</div>
                    <div className="jm-tags">
                      {pendingSkills.map((s, i) => (
                        <button
                          key={i}
                          className="jm-skill-btn"
                          onClick={() => removeSkill(s)}
                        >
                          ✕ {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="fit-done">
                <span className="fit-done-check">✓</span>
                <span>No remaining suggestions.</span>
                {numPages > 1 && (
                  <button
                    className="fit-btn fit-btn-ai"
                    onClick={() => handleFitToPage(fitTarget || 1)}
                    disabled={fitLoading}
                    style={{ marginTop: 8 }}
                  >
                    {fitLoading ? '⏳ Analyzing…' : 'Still too long? Analyze again'}
                  </button>
                )}
              </div>
            );
          })()}
            </div>
          )}
        </div>
      )}

      {(reorderSections && resume) && (
        <SectionOrderPanel
          settings={settings}
          resume={resume}
          reorderSections={reorderSections}
          toggleSectionVisibility={toggleSectionVisibility}
        />
      )}

      {densityPresets.length > 0 && (
        <div className="style-row">
          <label className="style-label">Density</label>
          <div className="separator-toggle row">
            {densityPresets.map((preset, i) => (
              <button
                key={preset.label}
                className={`sep-btn ${activeDensity === i ? 'active' : ''}`}
                onClick={() => applyDensityPreset && applyDensityPreset(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="style-row color-scheme-row">
        <label className="style-label">Color Scheme</label>
        <div className="color-scheme-swatches">
          {SCHEMES.map(s => (
            <button key={s.id} className={`color-swatch ${current === s.id ? 'active' : ''}`} style={{ background: s.color }} onClick={() => update('colorScheme', s.id)} title={s.label} />
          ))}
          {/* Custom color picker — rainbow swatch opens native color input */}
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button
              className={`color-swatch color-swatch-rainbow ${isCustom ? 'active' : ''}`}
              title="Custom color"
              onClick={() => customColorRef.current?.click()}
              style={isCustom ? { background: customColor } : { padding: 0, border: 'none' }}
            >
              {!isCustom && (
                <svg width="100%" height="100%" viewBox="-1.05 -1.05 2.1 2.1" style={{ display: 'block', pointerEvents: 'none' }}>
                  <path d="M0,0 L1,0 A1,1 0 0,1 0.866,0.5 Z" fill="hsl(0,100%,50%)"/>
                  <path d="M0,0 L0.866,0.5 A1,1 0 0,1 0.5,0.866 Z" fill="hsl(30,100%,50%)"/>
                  <path d="M0,0 L0.5,0.866 A1,1 0 0,1 0,1 Z" fill="hsl(60,100%,50%)"/>
                  <path d="M0,0 L0,1 A1,1 0 0,1 -0.5,0.866 Z" fill="hsl(90,100%,50%)"/>
                  <path d="M0,0 L-0.5,0.866 A1,1 0 0,1 -0.866,0.5 Z" fill="hsl(120,100%,50%)"/>
                  <path d="M0,0 L-0.866,0.5 A1,1 0 0,1 -1,0 Z" fill="hsl(150,100%,50%)"/>
                  <path d="M0,0 L-1,0 A1,1 0 0,1 -0.866,-0.5 Z" fill="hsl(180,100%,50%)"/>
                  <path d="M0,0 L-0.866,-0.5 A1,1 0 0,1 -0.5,-0.866 Z" fill="hsl(210,100%,50%)"/>
                  <path d="M0,0 L-0.5,-0.866 A1,1 0 0,1 0,-1 Z" fill="hsl(240,100%,50%)"/>
                  <path d="M0,0 L0,-1 A1,1 0 0,1 0.5,-0.866 Z" fill="hsl(270,100%,50%)"/>
                  <path d="M0,0 L0.5,-0.866 A1,1 0 0,1 0.866,-0.5 Z" fill="hsl(300,100%,50%)"/>
                  <path d="M0,0 L0.866,-0.5 A1,1 0 0,1 1,0 Z" fill="hsl(330,100%,50%)"/>
                </svg>
              )}
            </button>
            <input
              ref={customColorRef}
              type="color"
              value={isCustom ? customColor : '#2a6b6b'}
              style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
              onChange={e => update('colorScheme', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="style-row">
        <label className="style-label">Body Font</label>
        <select className="style-select" value={settings.bodyFont ?? 'DM Sans'} onChange={e => update('bodyFont', e.target.value)}>
          {FONT_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </div>

      {templateFeatures.photoPosition && (
        <div className="style-row">
          <label className="style-label">Photo Position</label>
          <div className="align-toggle">
            {['left', 'right'].map(pos => (
              <button key={pos} className={`align-btn ${(settings.photoPosition ?? 'left') === pos ? 'active' : ''}`} onClick={() => update('photoPosition', pos)}>
                <span>{pos.charAt(0).toUpperCase() + pos.slice(1)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="margin-diagram">
        <div className="margin-diagram-page">
          <div className="margin-diagram-top" style={{ height: `${(settings.marginTop / MARGIN_MAX) * 40 + 8}px` }}>
            <span>{settings.marginTop}"</span>
          </div>
          <div className="margin-diagram-middle">
            <div className="margin-diagram-left" style={{ width: `${(settings.marginLeft / MARGIN_MAX) * 40 + 8}px` }}>
              <span>{settings.marginLeft}"</span>
            </div>
            <div className="margin-diagram-content">
              <div className="margin-diagram-lines">
                {[80, 60, 70, 50, 65].map((w, i) => <div key={i} className="margin-diagram-line" style={{ width: `${w}%` }} />)}
              </div>
            </div>
            <div className="margin-diagram-right" style={{ width: `${(settings.marginRight / MARGIN_MAX) * 40 + 8}px` }}>
              <span>{settings.marginRight}"</span>
            </div>
          </div>
          <div className="margin-diagram-bottom" style={{ height: `${(settings.marginBottom / MARGIN_MAX) * 40 + 8}px` }}>
            <span>{settings.marginBottom}"</span>
          </div>
        </div>
      </div>

      <div className="margin-sliders">
        <div className="margin-slider-row">
          <div className="margin-slider-header">
            <span className="margin-icon">↕</span>
            <label className="style-label">Line Height</label>
            <EditableValue value={settings.lineHeight ?? 1.6} min={1.0} max={2.2} step={0.05} unit="×" decimals={2} onChange={v => update('lineHeight', v)} />
          </div>
          <input type="range" min={1.0} max={2.2} step={0.1} value={settings.lineHeight ?? 1.6} onMouseDown={onSliderMouseDown} onChange={e => (updateLive || update)('lineHeight', parseFloat(e.target.value))} onMouseUp={onSliderMouseUp} onTouchEnd={onSliderMouseUp} className="style-slider" />
          <div className="margin-slider-ticks"><span>1.0×</span><span>1.6×</span><span>2.2×</span></div>
        </div>
      </div>

      <div className="margin-sliders">
        {[
          { key: 'marginTop',    label: 'Top Margin',    icon: '↑' },
          { key: 'marginBottom', label: 'Bottom Margin', icon: '↓' },
          { key: 'marginLeft',   label: 'Left Margin',   icon: '←' },
          { key: 'marginRight',  label: 'Right Margin',  icon: '→' },
        ].map(({ key, label, icon }) => (
          <div className="margin-slider-row" key={key}>
            <div className="margin-slider-header">
              <span className="margin-icon">{icon}</span>
              <label className="style-label">{label}</label>
              <EditableValue value={settings[key]} min={MARGIN_MIN} max={MARGIN_MAX} step={0.05} unit='"' decimals={2} onChange={v => update(key, v)} />
            </div>
            <input type="range" min={MARGIN_MIN} max={MARGIN_MAX} step={MARGIN_STEP} value={settings[key]} onMouseDown={onSliderMouseDown} onChange={e => (updateLive || update)(key, parseFloat(e.target.value))} onMouseUp={onSliderMouseUp} onTouchEnd={onSliderMouseUp} className="style-slider" />
            <div className="margin-slider-ticks"><span>0.25"</span><span>1.00"</span><span>2.00"</span></div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn-reset-margins" style={{ flex: 1 }} onClick={reset}>↺ Reset page settings</button>
        {onMasterReset && (
          <button
            className="btn-reset-margins"
            style={{ flex: 1 }}
            onClick={onMasterReset}
          >
            ↺ Reset all to template defaults
          </button>
        )}
      </div>

      <div className="page-info-box">
        <div className="page-info-row"><span>Paper Size</span><strong>US Letter — 8.5 × 11 in</strong></div>
        <div className="page-info-row"><span>Orientation</span><strong>Portrait</strong></div>
        <div className="page-info-row"><span>Content Width</span><strong>{(8.5 - settings.marginLeft - settings.marginRight).toFixed(2)}" available</strong></div>
        <div className="page-info-row"><span>Content Height</span><strong>{(11 - settings.marginTop - settings.marginBottom).toFixed(2)}" per page</strong></div>
      </div>
    </div>
  );
}

// ── Shared Field ──────────────────────────────────────────
function Field({ label, value, onChange, placeholder, type = 'text', multiline = false, disabled = false }) {
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      {multiline ? (
        <textarea className="field-input" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={4} disabled={disabled} />
      ) : (
        <input className="field-input" type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} />
      )}
    </div>
  );
}

// ── Job Match ────────────────────────────────────────────
function JobMatchForm({ resume, addSkill, applyBulletSuggestion, applySummarySuggestion }) {
  const [url, setUrl] = useState('');
  const [jobText, setJobText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [dismissed, setDismissed] = useState(new Set());

  const norm = s => s.trim().toLowerCase().replace(/[\u2018\u2019\u201C\u201D]/g, "'").replace(/[\u2013\u2014]/g, '-').replace(/\s+/g, ' ');

  // Derive applied state from resume content (reacts to undo automatically)
  const isBulletApplied = (improved) => {
    const allBullets = (resume.experience || []).flatMap(exp => [
      ...(exp.bullets || []),
      ...(exp.subRoles || []).flatMap(sr => sr.bullets || []),
    ]).map(b => norm(b));
    return allBullets.includes(norm(improved));
  };
  const isSummaryApplied = analysis?.summarySuggestion &&
    norm(resume.personal?.summary || '') === norm(analysis.summarySuggestion);
  const isSkillAdded = (s) => (resume.skills || []).includes(s);

  const handleAnalyze = async () => {
    if (!url.trim() && !jobText.trim()) return;
    setLoading(true);
    setError(null);
    setAnalysis(null);
    setDismissed(new Set());
    try {
      const result = await analyzeJobMatch({
        url: url.trim() || undefined,
        jobText: jobText.trim() || undefined,
        resume,
      });
      setAnalysis(result);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const scoreColor = (score) => {
    if (score >= 75) return 'var(--teal)';
    if (score >= 50) return '#e6a817';
    return '#d94f4f';
  };

  return (
    <div className="jm-section">
      <h3 className="section-title">🎯 Job Match Analysis</h3>
      <p className="jm-intro">
        Paste a job posting URL or the description text to see how well your resume matches and get tailored suggestions.
      </p>

      {/* ── Input area ── */}
      <div className="jm-input-area">
        <div className="field">
          <label className="field-label">Job Posting URL</label>
          <input
            className="field-input"
            type="url"
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://boards.greenhouse.io/company/jobs/..."
            disabled={loading}
          />
        </div>

        <div className="jm-divider">
          <span className="jm-divider-line" />
          <span className="jm-divider-text">or paste below</span>
          <span className="jm-divider-line" />
        </div>

        <div className="field">
          <label className="field-label">Job Description</label>
          <textarea
            className="field-input"
            value={jobText}
            onChange={e => setJobText(e.target.value)}
            placeholder="Paste the full job description here..."
            rows={5}
            disabled={loading}
          />
        </div>

        <button
          className="jm-analyze-btn"
          onClick={handleAnalyze}
          disabled={loading || (!url.trim() && !jobText.trim())}
        >
          {loading ? '⏳ Analyzing…' : '🎯 Analyze Match'}
        </button>
      </div>

      {/* ── Error ── */}
      {error && <div className="jm-error">{error}</div>}

      {/* ── Results ── */}
      {analysis && (
        <div className="jm-results">

          {/* Score */}
          <div className="jm-score-card">
            <div className="jm-score-number" style={{ color: scoreColor(analysis.matchScore) }}>
              {analysis.matchScore}%
            </div>
            <div className="jm-score-label">Match Score</div>
            <div className="jm-score-bar-track">
              <div
                className="jm-score-bar-fill"
                style={{ width: analysis.matchScore + '%', background: scoreColor(analysis.matchScore) }}
              />
            </div>
            <p className="jm-match-summary">{analysis.matchSummary}</p>
          </div>

          {/* Keywords */}
          {(() => {
            const missingAll = [...new Set([...(analysis.keywordsMissing || []), ...(analysis.suggestedSkills || [])])].filter(s => !isSkillAdded(s));
            return (analysis.keywordsFound.length > 0 || missingAll.length > 0) && (
              <div className="jm-keywords-card">
                <h4 className="jm-card-title">Keywords</h4>
                {analysis.keywordsFound.length > 0 && (
                  <div className="jm-keyword-group">
                    <span className="jm-keyword-label">Found in your resume</span>
                    <div className="jm-tags">
                      {analysis.keywordsFound.map((k, i) => (
                        <span key={i} className="jm-tag jm-tag-found">{k}</span>
                      ))}
                    </div>
                  </div>
                )}
                {missingAll.length > 0 && (
                  <div className="jm-keyword-group">
                    <span className="jm-keyword-label">Missing — tap to add to your skills</span>
                    <div className="jm-tags">
                      {missingAll.map((s, i) => (
                        <button key={i} className="jm-skill-btn" onClick={() => addSkill(s)}>+ {s}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Bullet improvements */}
          {analysis.bulletImprovements.filter(b => !isBulletApplied(b.improved) && !dismissed.has(b.original)).length > 0 && (
            <div className="jm-bullets-card">
              <h4 className="jm-card-title">Suggested Bullet Improvements</h4>
              {analysis.bulletImprovements.filter(b => !isBulletApplied(b.improved) && !dismissed.has(b.original)).map((b, i) => (
                <div key={i} className="jm-bullet-item">
                  <div className="jm-bullet-current">
                    <span className="jm-bullet-label">Current</span>
                    <p>{b.original}</p>
                  </div>
                  <div className="jm-bullet-improved">
                    <span className="jm-bullet-label">Suggested</span>
                    <p>{b.improved}</p>
                  </div>
                  {b.reason && <p className="jm-bullet-reason">{b.reason}</p>}
                  <div className="jm-item-actions">
                    <button className="jm-apply-btn" onClick={() => applyBulletSuggestion(b.original, b.improved)}>Apply</button>
                    <button className="jm-discard-btn" onClick={() => setDismissed(prev => new Set([...prev, b.original]))}>Discard</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Summary suggestion */}
          {analysis.summarySuggestion && !isSummaryApplied && !dismissed.has('summary') && (
            <div className="jm-summary-card">
              <h4 className="jm-card-title">Suggested Summary</h4>
              <p className="jm-summary-text">{analysis.summarySuggestion}</p>
              <div className="jm-item-actions">
                <button className="jm-apply-btn" onClick={() => applySummarySuggestion(analysis.summarySuggestion)}>Apply</button>
                <button className="jm-discard-btn" onClick={() => setDismissed(prev => new Set([...prev, 'summary']))}>Discard</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
